import type { LayoutDocument, LayoutNode } from "../layout/types.js";
import type { RenderNode } from "../layout-compiler/types.js";

export type InterfaceDesign = "original" | "modern";
export type VisualNodePatch = Partial<
  Pick<LayoutNode, "x" | "y" | "width" | "height">
> & {
  style?: Record<string, string | number>;
};
/** Additive visual data only. Behavior, IDs, content and hierarchy stay in the original. */
export interface VisualDocumentVariant {
  sourceStructure?: string;
  canvas?: { width: number; height: number };
  backgroundColor?: string;
  nodes: Record<string, VisualNodePatch>;
}
export interface CellDesignVariant {
  version: 1;
  defaultWidth?: number;
  layout?: VisualDocumentVariant;
  headerLayout?: VisualDocumentVariant;
  headerGroup?: VisualDocumentVariant;
}
const styles = new Set([
  "color",
  "backgroundColor",
  "fontSize",
  "fontFamily",
  "fontWeight",
  "fontStyle",
  "textAlign",
  "borderColor",
  "borderWidth",
  "borderStyle",
  "borderRadius",
  "padding",
  "paddingLeft",
  "paddingRight",
]);
const geometry = new Set(["x", "y", "width", "height"]);
const record = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const dimension = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 && v <= 10000;
export function validVisualDocumentVariant(
  v: unknown,
): v is VisualDocumentVariant {
  if (
    !record(v) ||
    !record(v.nodes) ||
    Object.keys(v).some(
      (k) =>
        !["canvas", "backgroundColor", "nodes", "sourceStructure"].includes(k),
    )
  )
    return false;
  if (
    v.canvas !== undefined &&
    (!record(v.canvas) ||
      Object.keys(v.canvas).some((k) => !["width", "height"].includes(k)) ||
      !dimension(v.canvas.width) ||
      !dimension(v.canvas.height))
  )
    return false;
  if (v.sourceStructure !== undefined && typeof v.sourceStructure !== "string")
    return false;
  if (v.backgroundColor !== undefined && typeof v.backgroundColor !== "string")
    return false;
  return Object.entries(v.nodes).every(
    ([id, patch]) =>
      id !== "__proto__" &&
      id !== "constructor" &&
      id !== "prototype" &&
      record(patch) &&
      Object.entries(patch).every(([key, value]) => {
        if (geometry.has(key))
          return (
            typeof value === "number" &&
            Number.isFinite(value) &&
            (key === "x" || key === "y" || dimension(value))
          );
        return (
          key === "style" &&
          record(value) &&
          Object.entries(value).every(
            ([k, x]) =>
              styles.has(k) &&
              (typeof x === "string" ||
                (typeof x === "number" && Number.isFinite(x))),
          )
        );
      }),
  );
}
export function validCellDesignVariant(v: unknown): v is CellDesignVariant {
  return (
    record(v) &&
    v.version === 1 &&
    Object.keys(v).every((k) =>
      [
        "version",
        "defaultWidth",
        "layout",
        "headerLayout",
        "headerGroup",
      ].includes(k),
    ) &&
    (v.defaultWidth === undefined || dimension(v.defaultWidth)) &&
    ["layout", "headerLayout", "headerGroup"].every(
      (k) => v[k] === undefined || validVisualDocumentVariant(v[k]),
    )
  );
}
export function documentNodes(document: LayoutDocument): LayoutNode[] {
  const result: LayoutNode[] = [];
  const visit = (node: LayoutNode) => {
    result.push(node);
    if ("children" in node) node.children.forEach(visit);
  };
  document.scene.children.forEach(visit);
  return result;
}
export function visualSourceStructure(document: LayoutDocument): string {
  const visit = (node: LayoutNode): unknown => [
    node.id,
    node.kind,
    "primitive" in node
      ? node.primitive
      : "component" in node
        ? node.component
        : null,
    "children" in node ? node.children.map(visit) : null,
  ];
  return JSON.stringify(document.scene.children.map(visit));
}

/** If the source hierarchy changed, use the complete original instead of a partial variant. */
export function compatibleVisualVariant(
  document: LayoutDocument,
  variant?: VisualDocumentVariant,
): VisualDocumentVariant | undefined {
  if (!variant || !validVisualDocumentVariant(variant)) return undefined;
  if (
    variant.sourceStructure &&
    variant.sourceStructure !== visualSourceStructure(document)
  )
    return undefined;
  const ids = new Set(documentNodes(document).map((n) => n.id));
  return Object.keys(variant.nodes).every((id) => ids.has(id))
    ? variant
    : undefined;
}
/** Behavior-driven style properties retain priority over the new static appearance. */
function behaviorStyleKeys(document: LayoutDocument): Set<string> {
  const result = new Set<string>();
  const walk = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    if (
      record(value) &&
      value.type === "action" &&
      typeof value.key === "string"
    )
      result.add(value.key);
    for (const child of Object.values(value)) walk(child);
  };
  walk(document.scene);
  return result;
}
export function presentRenderNodes(
  nodes: RenderNode[],
  document: LayoutDocument,
  candidate?: VisualDocumentVariant,
): RenderNode[] {
  const variant = compatibleVisualVariant(document, candidate);
  if (!variant) return nodes;
  const dynamic = behaviorStyleKeys(document);
  const visit = (node: RenderNode): RenderNode => {
    const patch = Object.prototype.hasOwnProperty.call(variant.nodes, node.id)
      ? variant.nodes[node.id]
      : undefined;
    return {
      ...node,
      ...(patch
        ? Object.fromEntries(
            Object.entries(patch).filter(
              ([k]) => geometry.has(k) && !dynamic.has(k),
            ),
          )
        : {}),
      ...(patch?.style
        ? {
            style: {
              ...node.style,
              ...Object.fromEntries(
                Object.entries(patch.style).filter(([k]) => !dynamic.has(k)),
              ),
            },
          }
        : {}),
      ...("children" in node ? { children: node.children.map(visit) } : {}),
    } as RenderNode;
  };
  return nodes.map(visit);
}
/** Used for editor previews and grid measurements only, never as the runtime program. */
export function presentDocument(
  document: LayoutDocument,
  candidate?: VisualDocumentVariant,
): LayoutDocument {
  const variant = compatibleVisualVariant(document, candidate);
  if (!variant) return document;
  const visit = (node: LayoutNode): LayoutNode => {
    const patch = Object.prototype.hasOwnProperty.call(variant.nodes, node.id)
      ? variant.nodes[node.id]
      : undefined;
    return {
      ...node,
      ...patch,
      style: { ...node.style, ...patch?.style },
      ...("children" in node ? { children: node.children.map(visit) } : {}),
    } as LayoutNode;
  };
  return {
    ...document,
    meta: { ...document.meta, canvas: variant.canvas ?? document.meta.canvas },
    scene: {
      ...document.scene,
      backgroundColor:
        variant.backgroundColor ?? document.scene.backgroundColor,
      children: document.scene.children.map(visit),
    },
  };
}
export function readCellDesignVariant(
  cell: unknown,
  mode: InterfaceDesign,
): CellDesignVariant | undefined {
  if (mode !== "modern" || !record(cell)) return undefined;
  const v = cell.designVariants?.modern;
  if (!validCellDesignVariant(v)) return undefined;
  for (const [key, doc] of [
    ["layout", cell.layout],
    ["headerLayout", cell.headerLayout],
    ["headerGroup", cell.headerGroup?.layout],
  ] as const) {
    const patch = v[key];
    if (
      patch &&
      (!doc?.scene?.children || !compatibleVisualVariant(doc, patch))
    )
      return undefined;
  }
  return v;
}
/** Old clients omit extensions when editing. Explicit null still disables a cell. */
export function preserveCellDesignVariants(
  incoming: unknown,
  existing: unknown,
): unknown {
  if (
    !record(incoming) ||
    !record(existing) ||
    Object.prototype.hasOwnProperty.call(incoming, "designVariants")
  )
    return incoming;
  return existing.designVariants === undefined
    ? incoming
    : { ...incoming, designVariants: existing.designVariants };
}
